import React, {useEffect, useRef, useState} from "react";
import "./App.css";
import {FetchDataResponse, SelectedView} from "./types/types";
import {fetchTrees, getVersions} from "./api/Apis";
import TobBarTabs from "./components/TobBarTabs";
import {GENERAL_ERROR_TEXT} from "./consts/consts";
import {BrowserRouter as Router} from "react-router-dom";
import AppRoutes from "./components/AppRoutes";
import ScrollToTop from "./utils/ScrollToTop";

const App: React.FC = () => {
    const [view, setView] = useState<SelectedView>(SelectedView.Diagnosis);
    const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
    const [versions, setVersions] = useState<FetchDataResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [fetchedData, setFetchedData] = useState<Record<SelectedView, any[]>>({
        [SelectedView.Diagnosis]: [],
        [SelectedView.Procedures]: [],
        [SelectedView.Questionnaires]: [],
    });

    const fetchedViews = useRef(new Set<SelectedView>());

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const startTime = Date.now();
                const data = await getVersions();
                setVersions(data);

                if (view !== SelectedView.Questionnaires) {
                    const firstVersion = data[view]?.[0]?.version_id || null;
                    setSelectedVersion(firstVersion);
                }

                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(1000 - elapsedTime, 0);

                setTimeout(() => {
                    setLoading(false);
                }, remainingTime);
            } catch (error) {
                console.error(error);
                setError(GENERAL_ERROR_TEXT);
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (versions) {
            fetchAllDataSetsData();
        }
    }, [versions]);

    useEffect(() => {
        if (versions && view !== SelectedView.Questionnaires) {
            const firstVersion = versions[view]?.[0]?.version_id || null;
            setSelectedVersion(firstVersion);
        } else {
            setSelectedVersion(null);
        }
    }, [view, versions]);


    const fetchDataSetData = async (view: SelectedView, version: string) => {
        const data = await fetchTrees(view, version);
        setFetchedData((prevData) => ({
            ...prevData,
            [view]: data,
        }));
        fetchedViews.current.add(view);
    }

    const fetchAllDataSetsData = async () => {
        for (const view of Object.values(SelectedView)) {
            if (view === SelectedView.Questionnaires) continue;
            if (!versions || !versions[view]) continue;

            if (!fetchedViews.current.has(view)) {
                const firstVersion = versions[view][0]?.version_id;
                if (firstVersion) {
                    await fetchDataSetData(view, firstVersion);
                }
            }
        }
    };

    useEffect(() => {
        (async () => {
            try {
                if (view !== SelectedView.Questionnaires && !selectedVersion) return;

                const data = await fetchTrees(view, selectedVersion);
                setFetchedData((prevData) => ({
                    ...prevData,
                    [view]: data,
                }));
                fetchedViews.current.add(view);
            } catch (error) {
                console.error(error);
                setError(GENERAL_ERROR_TEXT);
            }
        })();
    }, [selectedVersion]);

    return (
        <div className="app">
            {loading ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                </div>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : (
                <Router>
                    <ScrollToTop/>
                    <TobBarTabs view={view} setView={setView}/>

                    <AppRoutes
                        fetchedData={fetchedData} versions={versions} selectedVersion={selectedVersion}
                        setSelectedVersion={setSelectedVersion} view={view}
                    />
                </Router>
            )}
        </div>
    );
};

export default App;